import { formatDistanceToNow } from "date-fns";
import React from "react";
import { useParams } from "react-router-dom";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import EmptyData from "@/components/state-pages/EmptyData";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Sheet from "@/components/ui/sheet";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { renderFullName } from "@/lib/utils";
import {
  useAddCommentsInToolsMutation,
  useGetSingleProductAssignmentQuery,
} from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

const CommentCard = ({
  name,
  time,
  comment,
  role,
}: {
  name: string;
  time: string;
  comment: string;
  role: string;
}): JSX.Element => {
  return (
    <section className='p-[14px] border border-input rounded '>
      <section>
        <div className='flex justify-between items-center font-medium  leading-6'>
          <h2 className='text-[16px] flex'>
            {name}
            <Badge className='ml-2 py-[2px] mb-2' variant={"primaryLight"}>
              {role}
            </Badge>
          </h2>
          <p className='text-[#6E6B7B] text-sm'>{time}</p>
        </div>
        <p className='max-w-[80%] text-sm text-black/80'>{comment}</p>
      </section>
    </section>
  );
};

interface CommentSheetProps {
  isOpen: boolean;
  toggleCommentSheet: () => void;
}

const CommentSheet = ({
  isOpen,
  toggleCommentSheet,
}: CommentSheetProps): JSX.Element => {
  const { id: projectId } = useParams();
  const { id, reset } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);
  const [addCommentsInTools, { isLoading }] = useAddCommentsInToolsMutation();
  const [comment, setIsComment] = React.useState<string>("");
  const { EMPTY_MESSAGE, CONTENT_TEXT, TOAST_MESSAGE } =
    PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS;

  const { data: singleData, ...rest } = useGetSingleProductAssignmentQuery({
    projectId: projectId!,
    productAssignmentId: id!,
  });

  const handleAddComment = async (): Promise<void> => {
    if (!comment) {
      customToast.error("Please enter a comment");

      return;
    }

    const response = await addCommentsInTools({
      data: {
        description: comment,
      },
      id: id!,
      projectId: projectId!,
    });

    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(TOAST_MESSAGE.COMMENT_ADDED);
        setIsComment("");
      },
    });
  };

  const handleKeyUp = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.key === "Enter") {
      await handleAddComment();
    }
  };

  const handleExit = (): void => {
    reset();
    toggleCommentSheet();
  };

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={handleExit}>
      <Sheet.SheetContent className='min-w-[40dvw]'>
        <ScrollArea className='h-[calc(100vh-4rem)] sidebar_scrollbar'>
          <FetchWrapper {...rest}>
            <Sheet.SheetHeader className='text-2xl font-bold '>
              Comments: {singleData?.product.name}
            </Sheet.SheetHeader>
            <div className=' space-y-3 mt-4  mb-14 pr-3'>
              {singleData?.comments?.length ? (
                singleData.comments.map(
                  ({ id, user, customerUser, createdAt, description }) => {
                    const { firstName, lastName, middleName } =
                      user || customerUser || {};

                    return (
                      <CommentCard
                        key={id}
                        name={renderFullName({
                          params: { firstName, lastName, middleName },
                        })}
                        time={formatDistanceToNow(createdAt, {
                          addSuffix: true,
                        })}
                        comment={description}
                        role={user?.role ?? ""}
                      />
                    );
                  },
                )
              ) : (
                <EmptyData title={EMPTY_MESSAGE.NO_COMMENTS_FOUND} />
              )}
              <div className='fixed bottom-0 w-[38%] bg-white dark:bg-primary py-5 z-10 '>
                <div className='relative'>
                  <Input
                    placeholder={CONTENT_TEXT.WRITE_COMMENT}
                    autoFocus
                    className='w-full px-2 py-[20px] '
                    value={comment}
                    onChange={(e) => setIsComment(e.target.value)}
                    onKeyUp={handleKeyUp}
                    disabled={isLoading}
                  />
                  <Button
                    className=' absolute right-1 top-1/2 transform -translate-y-1/2'
                    size={"sm"}
                    isLoading={isLoading}
                    onClick={handleAddComment}
                    disabled={isLoading}
                  >
                    {CONTENT_TEXT.SEND}
                  </Button>
                </div>
              </div>
            </div>
          </FetchWrapper>
        </ScrollArea>
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default CommentSheet;
